import { zodResolver } from '@hookform/resolvers/zod';
import { CalendarPlus, ListChecks, MapPin, Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { z } from 'zod';
import { PageHeader } from '../components/AppShell';
import { PageCaption } from '../components/PageCaption';
import { BulkBar, toggleAll, toggleId } from '../components/BulkBar';
import { ExportMenu } from '../components/ExportMenu';
import { Topbar } from '../components/Topbar';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Empty } from '../components/ui/empty';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Spinner } from '../components/ui/spinner';
import { Textarea } from '../components/ui/textarea';
import { useConfirm } from '../components/ui/confirm';
import { useToast } from '../components/ui/toast';
import { api } from '../lib/api/client';
import { useBulkDeleteEvents, useCreateEvent, useEvents } from '../lib/api/events';
import { exportEvents } from '../lib/api/export';
import { fmtDateTime, fmtRelative } from '../lib/date';
import { useLocaleStore } from '../lib/locales';
import { slugify } from '../lib/slug';
import type { EventInput, EventStatus } from '../lib/types';

const slugRe = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/i;

const eventSchema = z.object({
  slug: z.string().min(1).regex(slugRe),
  defaultLocale: z.string().regex(/^[a-z]{2,8}$/),
  status: z.enum(['draft', 'published', 'cancelled']).default('draft'),
  startAt: z.string().min(1),
  endAt: z.string().optional().or(z.literal('')),
  venue: z.string().max(512).optional().or(z.literal('')),
  venueLat: z.string().optional().or(z.literal('')),
  venueLon: z.string().optional().or(z.literal('')),
  registrationUrl: z.string().url().or(z.literal('')).optional(),
  // Initial translation values:
  title: z.string().min(1).max(512),
  summary: z.string().optional().or(z.literal('')),
  description: z.string().optional().or(z.literal('')),
});
type EventForm = z.input<typeof eventSchema>;

export default function Events(): JSX.Element {
  const locale = useLocaleStore((s) => s.current);
  const available = useLocaleStore((s) => s.available);
  const [statusFilter, setStatusFilter] = useState<'all' | EventStatus>('all');
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const [creating, setCreating] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const list = useEvents(
    {
      locale,
      status: statusFilter === 'all' ? undefined : statusFilter,
      page,
      pageSize,
    },
    { admin: true },
  );

  const create = useCreateEvent();
  const bulkDelete = useBulkDeleteEvents();
  const { showToast } = useToast();
  const { confirm, confirmDialog } = useConfirm();

  const total = list.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const pageIds = (list.data?.items ?? []).map((ev) => ev.id);

  return (
    <>
      <Topbar title="Events" subtitle={`Locale: ${locale.toUpperCase()}`} />
      <PageHeader
        title="Events"
        subtitle={`${total.toLocaleString()} event${total === 1 ? '' : 's'}`}
        actions={
          <div className="flex items-center gap-2">
            <Select
              value={statusFilter}
              onValueChange={(v) => {
                setStatusFilter(v as typeof statusFilter);
                setPage(1);
              }}
            >
              <SelectTrigger className="w-[160px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="published">Published</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
            <ExportMenu
              options={[
                {
                  format: 'csv',
                  label: 'CSV (current filters)',
                  description: 'Spreadsheet of events with venue and translation summary',
                  run: () =>
                    exportEvents({
                      format: 'csv',
                      locale,
                      status: statusFilter === 'all' ? undefined : statusFilter,
                    }),
                },
                {
                  format: 'ics',
                  label: 'iCalendar (.ics)',
                  description: 'Drop into Google Calendar / Outlook (skips cancelled)',
                  run: () =>
                    exportEvents({
                      format: 'ics',
                      locale,
                      status: statusFilter === 'all' ? undefined : statusFilter,
                    }),
                },
                {
                  format: 'json',
                  label: 'JSON',
                  description: 'Full event records including translations',
                  run: () =>
                    exportEvents({
                      format: 'json',
                      locale,
                      status: statusFilter === 'all' ? undefined : statusFilter,
                    }),
                },
              ]}
            />
            <Button onClick={() => setCreating(true)}>
              <Plus className="h-4 w-4" /> New event
            </Button>
          </div>
        }
      />
      <PageCaption id="events" icon={ListChecks}>
        <strong className="text-ink">Events</strong> — every event surfaced in the
        mobile app's Events tab. Use this to spot-check upcoming events before
        they go live, fix titles/locations inline, and verify localised
        translations have landed for each language audience.
      </PageCaption>
      <div className="px-6 pb-10">
        {list.isLoading ? (
          <Spinner label="Loading…" />
        ) : Array.isArray(list.data?.items) && list.data.items.length > 0 ? (
          <div className="overflow-hidden rounded-lg border border-line bg-surface shadow-card">
            <table className="table-base">
              <thead>
                <tr>
                  <th className="w-8">
                    <input
                      type="checkbox"
                      className="cursor-pointer"
                      checked={pageIds.length > 0 && pageIds.every((id) => selected.has(id))}
                      onChange={() => setSelected(toggleAll(selected, pageIds))}
                      aria-label="Select all"
                    />
                  </th>
                  <th>Event</th>
                  <th className="w-44">When</th>
                  <th className="w-44">Venue</th>
                  <th className="w-32">Status</th>
                  <th className="w-44">Locales</th>
                </tr>
              </thead>
              <tbody>
                {list.data.items.map((ev) => (
                  <tr key={ev.id} className={selected.has(ev.id) ? 'bg-brand/5' : ''}>
                    <td>
                      <input
                        type="checkbox"
                        className="cursor-pointer"
                        checked={selected.has(ev.id)}
                        onChange={() => setSelected(toggleId(selected, ev.id))}
                        aria-label={`Select ${ev.slug}`}
                      />
                    </td>
                    <td>
                      <div className="flex items-start gap-3">
                        {ev.imageUrl ? (
                          <img
                            src={ev.imageUrl}
                            alt=""
                            className="h-10 w-14 shrink-0 rounded object-cover"
                          />
                        ) : (
                          <div className="flex h-10 w-14 shrink-0 items-center justify-center rounded bg-surface-muted">
                            <CalendarPlus className="h-4 w-4 text-ink-subtle" />
                          </div>
                        )}
                        <div>
                          <Link
                            to={`/events/${ev.slug}`}
                            className="font-medium text-ink hover:text-brand"
                          >
                            {ev.title}
                          </Link>
                          <p className="font-mono text-xs text-ink-subtle">{ev.slug}</p>
                        </div>
                      </div>
                    </td>
                    <td className="text-sm">
                      <p className="text-ink">{fmtDateTime(ev.startAt)}</p>
                      <p className="text-xs text-ink-subtle">{fmtRelative(ev.startAt)}</p>
                    </td>
                    <td className="text-sm text-ink-muted">
                      {ev.venue ? (
                        <span className="inline-flex items-center gap-1">
                          <MapPin className="h-3 w-3" /> {ev.venue}
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td>
                      <Badge
                        variant={
                          ev.status === 'published'
                            ? 'success'
                            : ev.status === 'cancelled'
                              ? 'danger'
                              : 'warning'
                        }
                      >
                        {ev.status}
                      </Badge>
                    </td>
                    <td>
                      <div className="flex flex-wrap gap-1">
                        {(ev.availableLocales ?? []).map((l) => (
                          <Badge
                            key={l}
                            variant={l === ev.defaultLocale ? 'brand' : 'outline'}
                            className="uppercase"
                          >
                            {l}
                          </Badge>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex items-center justify-between border-t border-line px-4 py-3 text-sm text-ink-muted">
              <span>
                Page {page} of {totalPages}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <Empty
            icon={CalendarPlus}
            title="No events"
            description="Create an event with a starting time, venue and at least one translation."
            action={<Button onClick={() => setCreating(true)}>New event</Button>}
          />
        )}
      </div>

      <Dialog open={creating} onOpenChange={(o) => !o && setCreating(false)}>
        <DialogContent size="lg">
          <DialogHeader>
            <DialogTitle>New event</DialogTitle>
            <DialogDescription>
              You'll create the event metadata and the first locale's title in one step. Add more
              translations on the event detail page.
            </DialogDescription>
          </DialogHeader>
          <CreateEventForm
            defaultLocale={locale}
            availableLocales={available.map((l) => l.code)}
            busy={create.isPending}
            onCancel={() => setCreating(false)}
            onSubmit={async (form) => {
              const baseInput: EventInput = {
                slug: form.slug,
                defaultLocale: form.defaultLocale,
                status: form.status,
                startAt: new Date(form.startAt).toISOString(),
                endAt: form.endAt ? new Date(form.endAt).toISOString() : null,
                venue: form.venue || null,
                venueLat: form.venueLat || null,
                venueLon: form.venueLon || null,
                registrationUrl: form.registrationUrl || null,
              };
              let ev: { id: string; slug: string } | null = null;
              try {
                ev = await create.mutateAsync(baseInput);
                // The event row exists but has no translation yet — without the
                // translation upsert it'd render as title-less in lists. If this
                // second call fails, delete the orphan event so the user can retry
                // cleanly instead of accumulating duplicates.
                await api.put(`/admin/events/${ev.id}/translations/${form.defaultLocale}`, {
                  title: form.title,
                  summary: form.summary || null,
                  description: form.description || null,
                });
                showToast({ title: 'Event created', variant: 'success' });
                setCreating(false);
              } catch (err) {
                if (ev) {
                  // Best-effort cleanup; ignore failures here so the user still sees the original error.
                  await api.delete(`/admin/events/${ev.id}`).catch(() => undefined);
                }
                showToast({
                  title: 'Could not create',
                  description: (err as Error).message,
                  variant: 'danger',
                });
              }
            }}
          />
        </DialogContent>
      </Dialog>
      <BulkBar
        selected={selected}
        onClear={() => setSelected(new Set())}
        entityLabel="event"
        deleting={bulkDelete.isPending}
        onDelete={async () => {
          if (
            !(await confirm({
              title: `Permanently delete ${selected.size} event(s)?`,
              description: 'This deletes the selected events and all their translations. This cannot be undone.',
              confirmLabel: 'Delete',
            }))
          )
            return;
          try {
            const result = await bulkDelete.mutateAsync([...selected]);
            showToast({ title: `${result.deleted} event(s) deleted`, variant: 'success' });
            setSelected(new Set());
          } catch (err) {
            showToast({ title: 'Delete failed', description: (err as Error).message, variant: 'danger' });
          }
        }}
      />
      {confirmDialog}
    </>
  );
}

interface CreateEventFormProps {
  defaultLocale: string;
  availableLocales: string[];
  busy: boolean;
  onCancel: () => void;
  onSubmit: (values: EventForm) => void | Promise<void>;
}

function CreateEventForm({
  defaultLocale,
  availableLocales,
  busy,
  onCancel,
  onSubmit,
}: CreateEventFormProps): JSX.Element {
  // Fixed at mount. Recomputing `new Date()` per render changes `values`
  // every minute, and each change re-syncs the form — silently reverting
  // whatever the user hadn't typed into yet.
  const [nowLocalInput] = useState(() => new Date().toISOString().slice(0, 16));

  const form = useForm<EventForm>({
    resolver: zodResolver(eventSchema),
    // Keeps what the user has typed when this form re-syncs from the
    // server — a refetch or another admin's edit updates only the
    // fields they haven't touched, instead of wiping the whole form.
    resetOptions: { keepDirtyValues: true },
    values: {
      slug: '',
      defaultLocale,
      status: 'draft',
      startAt: nowLocalInput,
      endAt: '',
      venue: '',
      venueLat: '',
      venueLon: '',
      registrationUrl: '',
      title: '',
      summary: '',
      description: '',
    },
  });

  // Auto-derive slug from title until the user manually touches it.
  const watchedTitle = form.watch('title');
  const slugTouched = form.formState.dirtyFields.slug;
  useEffect(() => {
    if (slugTouched) return;
    const candidate = slugify(watchedTitle ?? '');
    if (candidate) form.setValue('slug', candidate, { shouldDirty: false });
  }, [watchedTitle, slugTouched, form]);

  return (
    <form
      onSubmit={form.handleSubmit(async (v) => onSubmit(eventSchema.parse(v)))}
      // Flex column so DialogBody claims the leftover space and the
      // DialogFooter stays pinned to the bottom on short viewports —
      // otherwise the Save/Cancel buttons get clipped when the 30-field
      // form is taller than the dialog's max-height.
      className="flex min-h-0 flex-1 flex-col gap-4"
    >
      <DialogBody className="grid gap-4">
      <div className="grid grid-cols-3 gap-3">
        <div className="col-span-2">
          <Label>Title (in {form.watch('defaultLocale').toUpperCase()})</Label>
          <Input {...form.register('title')} autoFocus />
          {form.formState.errors.title ? (
            <p className="mt-1 text-xs text-danger">{form.formState.errors.title.message}</p>
          ) : null}
        </div>
        <div>
          <Label>Slug</Label>
          <Input {...form.register('slug')} placeholder="auto-from-title" />
          {!slugTouched ? (
            <p className="mt-1 text-xs text-ink-subtle">Auto-generated. Edit to customise.</p>
          ) : null}
          {form.formState.errors.slug ? (
            <p className="mt-1 text-xs text-danger">{form.formState.errors.slug.message}</p>
          ) : null}
        </div>
      </div>

      <div>
        <Label>Summary</Label>
        <Textarea rows={2} {...form.register('summary')} />
      </div>
      <div>
        <Label>Description</Label>
        <Textarea rows={4} {...form.register('description')} />
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div>
          <Label>Default locale</Label>
          <Select
            value={form.watch('defaultLocale')}
            onValueChange={(v) => form.setValue('defaultLocale', v, { shouldDirty: true })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {availableLocales.map((l) => (
                <SelectItem key={l} value={l}>
                  {l.toUpperCase()}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Status</Label>
          <Select
            value={form.watch('status')}
            onValueChange={(v) => form.setValue('status', v as EventStatus, { shouldDirty: true })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="published">Published</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Registration URL</Label>
          <Input {...form.register('registrationUrl')} placeholder="https://…" />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Starts at</Label>
          <Input type="datetime-local" {...form.register('startAt')} />
        </div>
        <div>
          <Label>Ends at (optional)</Label>
          <Input type="datetime-local" {...form.register('endAt')} />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div className="col-span-1">
          <Label>Venue</Label>
          <Input {...form.register('venue')} placeholder="Radio House, Abuja" />
        </div>
        <div>
          <Label>Latitude</Label>
          <Input {...form.register('venueLat')} placeholder="9.0765" />
        </div>
        <div>
          <Label>Longitude</Label>
          <Input {...form.register('venueLon')} placeholder="7.3986" />
        </div>
      </div>
      </DialogBody>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={busy}>
          {busy ? 'Creating…' : 'Create event'}
        </Button>
      </DialogFooter>
    </form>
  );
}
